/*
 * DateCalendar.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * INVESTIGATION FINDINGS (Step 1):
 *
 * 1. Current date strip rendered in: src/pages/Ingestion.jsx (~lines 568-622)
 *    — a sticky div with recentDays.map() showing 7 pill buttons (±3 days).
 *
 * 2. Selected date state: `currentDate` in Ingestion.jsx — ISO string YYYY-MM-DD,
 *    initialised to today = new Date().toISOString().split('T')[0].
 *
 * 3. Date passed to forms: as `date={currentDate}` prop on every ManualEntry
 *    and BakeryEntry instance rendered by renderContent().
 *
 * 4. Date state location: entirely in Ingestion.jsx (parent). No form manages
 *    its own date state.
 *
 * 5. Entry indicator data: dayHasData(department, isoDate, dataType) reads
 *    localStorage synchronously. The parent computes datesWithEntries for the
 *    visible calendar month and passes them as Date[] prop. onMonthChange
 *    callback lets the parent recompute when the user navigates months.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'

export default function DateCalendar({
  selectedDate,
  onDateSelect,
  datesWithEntries = [],
  maxDate,
  onMonthChange,
}) {
  const max = maxDate || new Date()

  // Build a Set of ISO date strings for O(1) lookup in the day renderer
  const entrySet = new Set(
    datesWithEntries.map(d => {
      const iso = d instanceof Date ? d.toISOString().split('T')[0] : d
      return iso
    })
  )

  // Custom DayButton renders the day number plus a green dot if entry exists
  function CustomDayButton({ day, modifiers, children, ...buttonProps }) {
    const iso = day.date.toISOString().split('T')[0]
    const hasEntry = entrySet.has(iso)
    return (
      <button {...buttonProps}>
        {children}
        {hasEntry && <span className="rdp-entry-dot" aria-hidden="true" />}
      </button>
    )
  }

  return (
    <div className="rdp-staff-wrapper">
      <DayPicker
        mode="single"
        selected={selectedDate}
        onSelect={(date) => date && onDateSelect(date)}
        disabled={[{ after: max }]}
        onMonthChange={onMonthChange}
        navLayout="around"
        components={{ DayButton: CustomDayButton }}
        fixedWeeks
      />
    </div>
  )
}
